/**
 * Domain enumerations. Single source of truth for both API validation and UI rendering.
 * Values mirror Appendix A/B/C/D of the architecture spec, extended where market
 * benchmarks showed gaps (see docs/BENCHMARKS.md).
 */

export const USER_ROLES = ['admin', 'agent', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const CLIENT_TYPES = ['individual', 'company'] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

/**
 * Spec Appendix B, plus `roadside_assistance` (migration 0011).
 *
 * Οδική βοήθεια is a product of its own in this office — a separate contract from a separate
 * provider, with its own number, period and premium, written against a plate. The spec folded
 * it into motor cover; the archive did not, and neither does the renewals calendar. It sits
 * right after `auto` because the two are picked together and the select keeps this order.
 * Roadside cover bundled inside a motor policy stays `auto`: one contract, one number.
 */
export const INSURANCE_TYPES = [
  'auto',
  'roadside_assistance',
  'health',
  'property',
  'life',
  'travel',
  'liability',
  'marine',
  'professional_indemnity',
  'cyber',
  'other',
] as const;
export type InsuranceType = (typeof INSURANCE_TYPES)[number];

/**
 * ADDED (review pass 2): the lines every household is expected to hold. The client
 * card compares these against the client's live policies and surfaces the missing
 * ones as sales opportunities — the gap-analysis feature every benchmarked AMS ships.
 */
export const CORE_PRODUCT_TYPES: InsuranceType[] = ['auto', 'property', 'health', 'life'];

export const RENEWAL_FREQUENCIES = [
  'monthly',
  'quarterly',
  'semiannual',
  'annual',
  'one_off',
] as const;
export type RenewalFrequency = (typeof RENEWAL_FREQUENCIES)[number];

/** Number of months a single period spans. `one_off` never advances. */
export const FREQUENCY_MONTHS: Record<RenewalFrequency, number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
  one_off: 0,
};

export const POLICY_STATUSES = [
  'active',
  'pending',
  'expired',
  'cancelled',
  'renewal_due',
  'paid',
] as const;
export type PolicyStatus = (typeof POLICY_STATUSES)[number];

export const PAYMENT_STATUSES = ['unpaid', 'partially_paid', 'paid'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const CLAIM_STATUSES = [
  'open',
  'in_progress',
  'waiting_documents',
  'approved',
  'rejected',
  'paid',
  'closed',
] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

/** Claim states that mean "no longer being worked". Used for ageing/KPI queries. */
export const TERMINAL_CLAIM_STATUSES: ClaimStatus[] = ['rejected', 'paid', 'closed'];

export const REMINDER_STATUSES = ['pending', 'completed', 'overdue'] as const;
export type ReminderStatus = (typeof REMINDER_STATUSES)[number];

export const REMINDER_PRIORITIES = ['low', 'normal', 'high'] as const;
export type ReminderPriority = (typeof REMINDER_PRIORITIES)[number];

export const CLIENT_DOCUMENT_TYPES = [
  'id_card',
  'passport',
  'driving_license',
  'vehicle_reg',
  'company_doc',
  'contract',
  'other',
] as const;
export type ClientDocumentType = (typeof CLIENT_DOCUMENT_TYPES)[number];

/**
 * ADDED (benchmark gap #2): the spec had client- and claim-level documents but no
 * home for the policy contract itself — the single most-requested document in every
 * AMS reviewed. See docs/BENCHMARKS.md.
 */
export const POLICY_DOCUMENT_TYPES = [
  'policy_contract',
  'payment_notice',
  'endorsement',
  'green_card',
  'proposal',
  'cancellation',
  'other',
] as const;
export type PolicyDocumentType = (typeof POLICY_DOCUMENT_TYPES)[number];

/**
 * Carrier paperwork types.
 *
 * Offered as a picker so uploads are filed consistently, but NOT enforced by a database CHECK
 * the way client and policy types are (see migration 0007). Those two are closed sets the spec
 * enumerates; carrier paperwork is whatever a carrier sends, and a constraint guessed here
 * would reject a real document at the worst moment. `other` is the honest escape hatch.
 */
export const INSURER_DOCUMENT_TYPES = [
  'terms',
  'contract',
  'commission_agreement',
  'product_sheet',
  'price_list',
  'circular',
  'other',
] as const;
export type InsurerDocumentType = (typeof INSURER_DOCUMENT_TYPES)[number];

export const CLAIM_TIMELINE_ACTIONS = [
  'created',
  'status_changed',
  'document_uploaded',
  'note_added',
  'contact_insurer',
  'payout_received',
  'follow_up',
] as const;
export type ClaimTimelineAction = (typeof CLAIM_TIMELINE_ACTIONS)[number];

/**
 * ADDED (benchmark gap #3): insured risk objects. Greek auto policies are worked by
 * plate number (`αριθμός κυκλοφορίας`); property policies by address. Without this the
 * agent cannot answer "which policy covers ΙΧΑ-1234?".
 */
export const INSURED_ITEM_TYPES = ['vehicle', 'property', 'person', 'vessel', 'other'] as const;
export type InsuredItemType = (typeof INSURED_ITEM_TYPES)[number];

/**
 * ADDED (migration 0010): the vehicle categories a carrier prices commission by.
 *
 * Not a cosmetic label — this is the lookup key into `insurer_commission_rates`. Every insurer
 * pays a different percentage per category, so «τι προμήθεια έχει αυτό το συμβόλαιο» cannot be
 * answered by the carrier alone.
 *
 * The list is the office's four (ΙΧ επιβατικό, ΕΙΧ ενοικιαζόμενο, ΦΙΧ φορτηγό, ΜΟΤΟ) plus the
 * ones the archive actually contains — the carriers' own tariff grids enumerate Ε.Ι.Χ., Φ.Ι.Χ.,
 * Φ.Ι.Χ. ΑΓΡΟ, Φ.Δ.Χ., Λ.Ι.Χ., MOTO, ΤΑΞΙ, ΑΥΤ.ΡΥΜ. and ΤΡΑΚΤΕΡ, and the book has tourist
 * coaches and rented mopeds in it.
 *
 * Note the naming trap for anyone reading the paperwork: on a Greek policy `Ε.Ι.Χ.` means
 * Επιβατικό Ιδιωτικής Χρήσης — a private car, NOT a rental. Rentals are written with an
 * ΕΝΟΙΚ… suffix («ΕΙΧ ΜΟΤ/ΤΑ ΕΝΟΙΚ»), which is why the two are separate values here.
 */
export const VEHICLE_CATEGORIES = [
  'passenger_private',
  'passenger_rental',
  'taxi',
  'truck_private',
  'truck_public',
  'agricultural',
  'bus',
  'motorcycle',
  'motorcycle_rental',
  'trailer',
  'tractor',
  'other',
] as const;
export type VehicleCategory = (typeof VEHICLE_CATEGORIES)[number];

/**
 * ADDED (benchmark gap #4): premium instalments (`δόσεις`) and the payment notices
 * (`ειδοποιητήρια`) insurers issue for them.
 */
export const INSTALLMENT_STATUSES = ['pending', 'paid', 'overdue', 'cancelled'] as const;
export type InstallmentStatus = (typeof INSTALLMENT_STATUSES)[number];

/**
 * Where a period stands on its ειδοποιητήριο — the insurer's renewal notice.
 *
 * The office's actual renewal sequence is: the carrier issues a notice, the agent rings the
 * client about ten days out («λήγει σε Χ μέρες, ανανεώνουμε;»), and the client answers. Until
 * now none of that was recorded anywhere, so the renewals board could say what was expiring
 * but not what had been *done* about it — which is the only question a morning triage asks.
 *
 * Four states, not a boolean, because a tick collapses the two answers that lead to opposite
 * work: «τον κάλεσα και περιμένω» keeps the policy on the list, «είπε όχι» takes it off and
 * turns it into a win-back. `declined` is deliberately not `cancelled` — the client refusing
 * to renew is not the policy being cancelled mid-term, and conflating them would corrupt the
 * cancellation figures.
 */
export const RENEWAL_NOTICE_STATUSES = ['pending', 'notified', 'confirmed', 'declined'] as const;
export type RenewalNoticeStatus = (typeof RENEWAL_NOTICE_STATUSES)[number];

export const PAYMENT_METHODS = ['cash', 'card', 'bank_transfer', 'direct_debit', 'cheque'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * ADDED (benchmark gap #5): commission reconciliation against carrier statements —
 * rated "important or highly important" by 73% of reviewers in the market data.
 */
export const COMMISSION_STATUSES = ['expected', 'statement_received', 'settled', 'disputed'] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];

export const SETTLEMENT_STATUSES = ['draft', 'reconciling', 'closed'] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

/**
 * ADDED (benchmark gap #6): endorsements — `πρόσθετες πράξεις`. Mid-term amendments
 * that change cover, premium or payment frequency without starting a new period.
 * Universal in Greek agency software.
 */
export const ENDORSEMENT_TYPES = [
  'coverage_change',
  'premium_adjustment',
  'vehicle_change',
  'driver_change',
  'address_change',
  'frequency_change',
  'suspension',
  'reinstatement',
  'other',
] as const;
export type EndorsementType = (typeof ENDORSEMENT_TYPES)[number];

/** ADDED (benchmark gap #9): CRM-style communication log per client. */
export const ACTIVITY_TYPES = ['call', 'email', 'meeting', 'note', 'sms', 'visit'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ACTIVITY_DIRECTIONS = ['inbound', 'outbound'] as const;
export type ActivityDirection = (typeof ACTIVITY_DIRECTIONS)[number];

/** ADDED (benchmark gap #8): in-app notification centre. */
export const NOTIFICATION_TYPES = [
  'renewal_due',
  'policy_expired',
  'reminder_overdue',
  'claim_status_changed',
  'installment_overdue',
  'assigned',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** ADDED (benchmark gap #1): audit trail, promoted from "future" to MVP for compliance. */
export const AUDIT_ACTIONS = ['create', 'update', 'delete', 'login', 'export', 'import'] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDITED_ENTITIES = [
  'user',
  'client',
  'policy',
  'claim',
  'reminder',
  'insurance_company',
  'document',
  'endorsement',
  'installment',
  'commission_settlement',
  'auth',
] as const;
export type AuditedEntity = (typeof AUDITED_ENTITIES)[number];

/** Entity kinds a reminder or activity can point at. */
export const LINKABLE_ENTITIES = ['client', 'policy', 'claim'] as const;
export type LinkableEntity = (typeof LINKABLE_ENTITIES)[number];

export const SUPPORTED_LOCALES = ['el', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
